import * as React from 'react'
import { z } from 'zod'
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog'

/** What Pixel Sign's page tells the window it's in: who signed, all signed, or declined. */
const signingMessageSchema = z.object({ source: z.literal('pixelsign'), type: z.enum(['signed', 'completed', 'declined']) })
export type SigningOutcome = z.infer<typeof signingMessageSchema>['type']

interface SigningFrameProps {
  /** Pixel Sign's page for this signer, or null while it's closed. */
  embedUrl: string | null
  title: string
  description: string
  onClose: () => void
  /** A hint only: Pixel Sign's webhook decides what happened. */
  onOutcome: (outcome: SigningOutcome) => void
}

/**
 * Pixel Sign's signing page inside the app (docs/sales.spec.md, "Choosing and
 * signing"), in a large dialog. It listens for the page saying it's done, from
 * Pixel Sign's own origin and nobody else's.
 */
export function SigningFrame({ embedUrl, title, description, onClose, onOutcome }: SigningFrameProps) {
  React.useEffect(() => {
    if (embedUrl === null) return undefined
    const origin = new URL(embedUrl).origin
    const listen = (event: MessageEvent<string | object | null>): void => {
      if (event.origin !== origin) return
      const message = signingMessageSchema.safeParse(event.data)
      if (message.success) onOutcome(message.data.type)
    }
    window.addEventListener('message', listen)
    return () => window.removeEventListener('message', listen)
  }, [embedUrl, onOutcome])

  return (
    <Dialog open={embedUrl !== null} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-5xl">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>
        {embedUrl === null ? null : <iframe title={title} src={embedUrl} allow="clipboard-write" className="h-[75vh] w-full rounded-lg border border-line" />}
      </DialogContent>
    </Dialog>
  )
}
